import { AfterViewInit, Component, OnInit, ViewChild } from '@angular/core';
import { switchMap } from 'rxjs/operators';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { FeatureFlag } from '../../../../models/entities/feature-flag';
import { AvailableFeatureFlag } from '../../../../models/entities/available-feature-flag';
import { FeatureFlagsService } from '../../../../services/organization/feature-flags.service';
import { PropertyManagementCompanyDataService } from '../../../../services/property-management-company/property-manager-company-data.service';
import { NgIf } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { PageHeaderComponent } from '../../shared/page-header/page-header.component';
import { AppTableComponent } from '../../shared/table/table.component';
import { TableTypeNames } from '../../../../services/column.service';
import { ColumnMap } from '../../../../models/entities/column-map';
import { EmptyStateModule } from '../../shared/empty-state/empty-state.module';
import { ScrollService } from '../../../../services/scroll.service';
import { GrowlerService } from '../../../../services/growler.service';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { ConfirmActionV2DialogComponent, ConfirmActionV2DialogInput } from '../../shared/dialogs/confirm-action-dialog/confirm-action-dialog.component';
import { MatDialogSizes } from '../../../../enums/mat-dialog-sizes.enum';
import { FormControl, Validators } from '@angular/forms';
import { AvatarBlockModule } from '../../shared/avatar-block/avatar-block.module';
import { MatSortModule } from '@angular/material/sort';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { EmptyCellDirective, JustifyRightCellDirective } from '../../shared/table/table.directive';
import { LegfiDatePipeModule } from '../../../../pipes/format/legfi-date.module';
import { ButtonDirective } from '../../shared/directives/button.directive';
import { MatListModule } from '@angular/material/list';
import { MatMenuModule } from '@angular/material/menu';

@UntilDestroy()
@Component({
    selector: ' app-pmc-feature-flags',
    templateUrl: './pmc-feature-flags.component.html',
    standalone: true,
    imports: [
        PageHeaderComponent,
        AppTableComponent,
        MatButtonModule,
        NgIf,
        MatIconModule,
        EmptyStateModule,
        MatDialogModule,
        AvatarBlockModule,
        MatSortModule,
        MatTableModule,
        EmptyCellDirective,
        LegfiDatePipeModule,
        JustifyRightCellDirective,
        ButtonDirective,
        MatListModule,
        MatMenuModule,
    ],
})
export class PmcFeatureFlagsComponent implements OnInit, AfterViewInit
{
    @ViewChild(AppTableComponent) tableRef: AppTableComponent<FeatureFlag>;

    readonly tableType = TableTypeNames.PMC_FEATURE_FLAGS;
    readonly columnMap: ColumnMap[] = [
        {key: 'mobile', title: 'Feature Flag', exportAs: 'flag', isMobile: true},
        {key: 'name', title: 'Feature Flag', exportAs: 'flag'},
        {key: 'enabled', title: 'Enabled At', exportAs: 'enabled'},
        {key: 'actions', title: 'Actions', exportAs: 'actions', disabled: () => true},
    ];

    availableFeatureFlags: AvailableFeatureFlag[] = [];

    constructor(
            private _propertyManagementCompanyData: PropertyManagementCompanyDataService,
            private _featureFlagsService: FeatureFlagsService,
            private _dialog: MatDialog,
            private _scrollService: ScrollService,
            private _growler: GrowlerService,
    ) {
    }

    get pmcId()  {
        return this._propertyManagementCompanyData.pmcId;
    }

    ngOnInit() {
        this._scrollService.scrollToTop();
    }

    ngAfterViewInit() {
        this.setupTable();
    }

    createFlag() {
        const options = this.availableFeatureFlags.filter((availableFlag) => {
            const datasource = this.tableRef.data as MatTableDataSource<FeatureFlag>;
            if (!datasource.data) {
                return true;
            }
            return !datasource.data.some((activeFlag) => activeFlag.featureName === availableFlag.featureName);
        }).map((flag) => {
            return {
                featureName: flag.featureName,
                label: flag.name,
            };
        });

        let data: ConfirmActionV2DialogInput;
        if (options.length === 0) {
            data = {
                title: 'No Feature Flags Available',
                content: 'There are no available feature flags to add to this organization.',
                confirmButton: 'OK',
                hideCancelButton: true,
            };
            this._dialog.open(ConfirmActionV2DialogComponent, {
                panelClass: ['v2-dialog', 'confirmation-layout'],
                width: MatDialogSizes.SM,
                autoFocus: 'dialog',
                data,
            });
            return;
        }

        const featureFlagsControl = new FormControl<{
            featureName: string
            label: string
        }>(null, Validators.required);
        data = {
            title: 'Add Feature Flag',
            content: `Select a feature flag to add to this PMC`,
            confirmButton: 'Yes, add this feature flag',
            cancelButton: 'No',
            control: {
                control: featureFlagsControl,
                type: 'select',
                label: `Available Feature Flags`,
                options,
            },
        };
        this._dialog.open(ConfirmActionV2DialogComponent, {
            panelClass: ['v2-dialog', 'confirmation-layout'],
            width: MatDialogSizes.SM,
            autoFocus: 'dialog',
            data,
        }).afterClosed().subscribe({
            next: (res) => {
                if (res?.success) {
                    this.tableRef.loading = true;
                    this._featureFlagsService.createFeatureFlag('pmc', this.pmcId, featureFlagsControl.value.featureName).pipe(
                            untilDestroyed(this),
                    ).subscribe({
                        next: () => {
                            this._growler.success('Success', 'Feature flag successfully added');
                            this.tableRef.refresh();
                        },
                        error: () => this._growler.oops('Unable to add feature flag'),
                    });
                }
            },
        });
    }

    deleteFlag(flag: FeatureFlag) {
        const data = {
            title: 'Remove Feature Flag',
            content: `Are you sure you want to remove the "${flag.name}" feature flag?`,
            confirmButton: 'Yes, remove this feature flag',
            cancelButton: 'No',
        };
        this._dialog.open(ConfirmActionV2DialogComponent, {
            panelClass: ['v2-dialog', 'confirmation-layout'],
            width: MatDialogSizes.SM,
            autoFocus: 'dialog',
            data,
        }).afterClosed().subscribe({
            next: (res) => {
                if (res?.success) {
                    this.tableRef.loading = true;
                    this._featureFlagsService.deleteFeatureFlag('pmc', this.pmcId, flag.featureName).pipe(
                            untilDestroyed(this),
                    ).subscribe({
                        next: () => {
                            this._growler.success('Success', 'Feature flag successfully removed');
                            this.tableRef.refresh();
                        },
                        error: () => this._growler.oops('Unable to remove feature flag'),
                    });
                }
            },
        });
    }

    private setupTable() {
        this.tableRef.connectTable$.pipe(
                switchMap(() => this._featureFlagsService.getAvailableFeatureFlags('pmc')),
                switchMap((flags) => {
                    this.availableFeatureFlags = flags;

                    if (this.pmcId) {
                        return this._featureFlagsService.getFeatureFlags('pmc', this.pmcId);
                    }

                    return;
                }),
                untilDestroyed(this),
        ).subscribe({
            next: (res) => this.tableRef.data$.next(res),
            error: () => {
                this.tableRef.loading = false;
                this._growler.oops('Unable to load feature flags');
            },
        });
    }
}
